<template>
  <div class="config-import-file">
    <div class="max-w-400 mb-4">
      <DropFileArea
        ref="dropFileAreaRef"
        :description="$t('audience-new.config-import.drop-file.description')"
        :maxFileSizeBytes="2000000000 /* 2GB */"
        :acceptedFileTypes="acceptedFileTypes"
        :disabled="isLoadingStep"
        @files-selected="handleSelectedFile"
      />
    </div>
    <div class="mb-5 max-w-633">
      <CAlert class="mb-0 font-sm" color="info">
        <div class="d-flex align-items-baseline text-black">
          <div>
            <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="15" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
          </div>
          <p class="mb-0">
            {{ $t('audience-new.config-import.is-phone-same-country-info-file') }}
          </p>
        </div>
      </CAlert>
    </div>
    <span class="font-weight-semi-bold font-xl mb-3 d-block">{{ $t('audience-new.config-import.detail-contacts') }}</span>
    <div class="mb-5">
      <span class="mb-2 d-block font-weight-semi-bold">{{ $t('audience-new.config-import.is-first-row-header') }}</span>
      <div class="d-flex gap-15 align-items-center">
        <div class="d-flex align-items-center min-w-100">
          <CSwitch :checked.sync="isFirstLineHeaders" :disabled="isLoadingStep" class="switch-custom" color="primary" shape="pill" size="lg" />
          <span class="ml-2 align-self-center">{{
            isFirstLineHeaders ? $t('audience-new.config-import.switch.yes') : $t('audience-new.config-import.switch.no')
          }}</span>
        </div>
        <div>
          <div v-if="isUploadedFileCSV" class="border font-xs p-2 min-w-290 d-flex">
            <span class="mr-2 align-self-center">{{ $t('audience-new.config-import.sample.example') }}:</span>
            <span>
              {{ $t('audience-new.config-import.sample.name') }}{{ CSVDelimiterChar }} {{ $t('audience-new.config-import.sample.contact')
              }}{{ CSVDelimiterChar }} E-mail <br />
              {{ $t('audience-new.config-import.sample.name-label') }}{{ CSVDelimiterChar }} 351567879065{{ CSVDelimiterChar }}
              {{ $t('audience-new.config-import.sample.email-label') }}
            </span>
          </div>
          <table v-else class="border sample-table d-flex">
            <span class="pl-2 align-self-center">{{ $t('audience-new.config-import.sample.example') }}:</span>
            <div class="p-2">
              <tr>
                <th>{{ $t('audience-new.config-import.sample.name') }}</th>
                <th>{{ $t('audience-new.config-import.sample.contact') }}</th>
                <th>Email</th>
              </tr>
              <tr>
                <td>{{ $t('audience-new.config-import.sample.name-label') }}</td>
                <td>351567879065</td>
                <td>{{ $t('audience-new.config-import.sample.email-label') }}</td>
              </tr>
            </div>
          </table>
        </div>
      </div>
    </div>
    <div v-if="isUploadedFileCSV" class="mb-5">
      <span class="mb-2 d-block font-weight-semi-bold">{{ $t('audience-new.config-import.how-is-file-separated') }} <span class="text-danger">*</span></span>
      <div class="d-flex gap-15 align-items-center">
        <TypeDemiliterCSVExport @delimiterValue="setDelimiterValue" :disabled="isLoadingStep" class="mb-0" />
        <p class="border font-xs p-2 min-w-290 mb-0">
          <span class="mr-2 align-self-center">{{ $t('audience-new.config-import.sample.example') }}:</span>
          <span
            >{{ $t('audience-new.config-import.sample.name-label') }}{{ CSVDelimiterChar }} 351567879065{{ CSVDelimiterChar }}
            {{ $t('audience-new.config-import.sample.email-label') }}</span
          >
        </p>
      </div>
    </div>
    <div class="mb-4">
      <span class="mb-2 d-block font-weight-semi-bold">{{ $t('audience-new.config-import.is-blacklist') }}</span>
      <div class="d-flex gap-15 align-items-center">
        <div class="d-flex align-items-center min-w-100">
          <CSwitch
            :checked.sync="isBlackList"
            :disabled="isLoadingStep"
            class="switch-custom"
            color="primary"
            shape="pill"
            size="lg"
            @update:checked="handleIsBlackListUpdate"
          />
          <span class="ml-2">{{ isBlackList ? $t('audience-new.config-import.switch.yes') : $t('audience-new.config-import.switch.no') }}</span>
        </div>
        <div class="d-flex gap-15">
          <div class="d-flex checkbox-area">
            <CInputCheckbox custom :checked.sync="blackListChannels.email" :disabled="!isBlackList || isLoadingStep" class="mr-2" />
            <span>E-mails</span>
          </div>
          <div class="d-flex checkbox-area">
            <CInputCheckbox custom :checked.sync="blackListChannels.sms" :disabled="!isBlackList || isLoadingStep" class="mr-2" />
            <span>SMS</span>
          </div>
        </div>
      </div>
      <div class="mt-3 d-flex align-items-baseline font-sm">
        <div>
          <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="16" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
        </div>
        <p class="mb-0 max-w-633">
          {{ $t('audience-new.config-import.is-blacklist-info') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import DropFileArea from '@/components/DropFileArea.vue'
import TypeDemiliterCSVExport from '@/components/TypeDemiliterCSVExport.vue'
import vSelect from 'vue-select'
import { removeCountriesWithoutCallingCode } from '@/utils/countriesUtils.js'
import services from '@/services.js'
import { IMPORT_CONTACTS_STEPS } from '../../../constants'

export default {
  name: 'ConfigImportFile',
  components: { DropFileArea, TypeDemiliterCSVExport, vSelect },
  data() {
    return {
      isFirstLineHeaders: false,
      isBlackList: false,
      blackListChannels: {
        sms: false,
        email: false,
      },
      acceptedFileTypes: ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      uploadedFile: undefined,
      CSVDelimiterChar: undefined,
      isLoadingStep: false,
      IMPORT_CONTACTS_STEPS,
    }
  },

  methods: {
    handleSelectedFile(file) {
      this.uploadedFile = file
      this.$store.dispatch('audience/setImportContactsValidSteps', [this.$store.state.audience.importContacts.currentStep])
    },

    handleIsBlackListUpdate(isChecked) {
      if (isChecked) return

      this.blackListChannels = {
        sms: false,
        email: false,
      }
    },
    setDelimiterValue(char) {
      this.CSVDelimiterChar = char
    },

    async sendFile() {
      try {
        this.isLoadingStep = true

        const formData = new FormData()

        formData.append('name', this.uploadedFile.name)
        formData.append('File', this.uploadedFile)
        formData.append('IsFirstLineHeaders', this.isFirstLineHeaders)

        let subscriptionChannels = ['EMAIL', 'SMS']
        if (this.isBlackList) {
          subscriptionChannels = subscriptionChannels.filter((channel) => {
            if (this.blackListChannels.email && channel === 'EMAIL') return false
            if (this.blackListChannels.sms && channel === 'SMS') return false
            return true
          })
        }

        formData.append('Subscriptions', subscriptionChannels)

        formData.append('ContactsAreSubscribed', true)

        formData.append('PhoneCountryIndicator', 0) // * Cliente lida com indicador de país no arquivo

        if (this.CSVDelimiterChar) formData.append('CSVDelimiterChar', this.CSVDelimiterChar)

        const response = await services.sendFile(this.organizationId, formData)

        this.$store.dispatch('audience/setImportedData', response.data.data)

        const { completedSteps, currentStep } = this.$store.state.audience.importContacts

        this.$store.dispatch('audience/setImportContactsCompletedSteps', [...completedSteps, currentStep])
        this.$store.dispatch('audience/setImportContactsStep', IMPORT_CONTACTS_STEPS.MATCH_COLUMNS)
        this.clearFormData()
      } catch (error) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.message || error?.response?.data?.messages[0]?.error?.code

        if (!errorCode) {
          this.$emit('addMessage', {
            status: 'danger',
            text: this.$t(`error.codes.DEFAULT`),
          })
          return
        }

        this.$emit('addMessage', {
          status: 'danger',
          text: this.$t(`general.errors.${errorCode}`),
        })
      } finally {
        this.isLoadingStep = false
      }
    },

    clearFormData() {
      this.$refs.dropFileAreaRef.clearFiles()
      this.isFirstLineHeaders = false
      this.isBlackList = false
      this.blackListChannels = {
        sms: false,
        email: false,
      }
      this.uploadedFile = undefined
      this.CSVDelimiterChar = undefined
      this.phoneCountryIndicator = undefined
    },

    checkAndSetValidStep() {
      if (!this.uploadedFile || (this.isUploadedFileCSV && !this.CSVDelimiterChar)) {
        this.$store.dispatch('audience/setImportContactsValidSteps', [])
        return
      }
      this.$store.dispatch('audience/setImportContactsValidSteps', [this.$store.state.audience.importContacts.currentStep])
    },
  },

  computed: {
    organizationId() {
      return this.$route.params.organizationId
    },
    isUploadedFileCSV() {
      return this.uploadedFile?.type === this.acceptedFileTypes[0] || this.uploadedFile?.type === this.acceptedFileTypes[1]
    },
    optionsCountries() {
      return removeCountriesWithoutCallingCode(this)
    },
  },

  watch: {
    isLoadingStep(newValue) {
      this.$emit('changeLoadingStep', newValue)
    },
    uploadedFile() {
      this.checkAndSetValidStep()
    },
    CSVDelimiterChar() {
      this.checkAndSetValidStep()
    },
  },
}
</script>
<style lang="scss">
.config-import-file {
  .switch-custom span {
    background-color: var(--gray-300);
  }

  .checkbox-area .custom-control-label::before {
    top: 0.13rem;
  }
  .checkbox-area .custom-control-label::after {
    top: 0.13rem;
  }
}
</style>

<style lang="scss" scoped>
.gap-15 {
  gap: 30px;
}
.sample-table {
  font-size: 0.75rem;
  th,
  td {
    font-weight: normal;
    text-align: center;
    padding: 5px;
  }
}
.min-w-100 {
  min-width: 100px;
}
.max-w-400 {
  max-width: 400px;
}
.max-w-633 {
  max-width: 633px;
}
.min-w-290 {
  min-width: 290px;
}
.min-w-230 {
  min-width: 230px;
}
</style>
